import isHotkey from 'is-hotkey';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading1,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Underline,
} from 'lucide-react';
import {useCallback, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import type {FormEvent, ReactNode} from 'react';
import {createPortal} from 'react-dom';
import type {Descendant, NodeEntry} from 'slate';
import {
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Range,
  Transforms,
  createEditor,
} from 'slate';
import type {HistoryEditor} from 'slate-history';
import {withHistory} from 'slate-history';
import type {RenderElementProps, RenderLeafProps} from 'slate-react';
import {Editable, ReactEditor, Slate, useSlate, withReact} from 'slate-react';

import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Separator} from '@common-web/components/ui/separator';
import {Toggle} from '@common-web/components/ui/toggle';
import type {
  CustomElement,
  LinkElement,
  MentionElement,
  SlateValue,
  TextAlign,
} from '@common-web/lib/slate-types';
import {initialsFromFullName} from '@common-web/lib/nameUtils';
import {cn} from '@common-web/lib/utils';

const HOTKEYS: Record<string, string> = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'] as const;
const TEXT_ALIGN_TYPES: readonly TextAlign[] = ['left', 'center', 'right', 'justify'] as const;

type BlockFormat = CustomElement['type'] | TextAlign;
type MarkFormat = 'bold' | 'italic' | 'underline' | 'code';
type AppEditor = Editor & ReactEditor & HistoryEditor;

/**
 * Register `link` and `mention` elements as inline so Slate flows them within
 * text blocks. Mentions are additionally void (their display text lives on the
 * node, not in an editable child) and markable so toggling bold/italic with a
 * mention selected doesn't throw.
 */
function withInlines<T extends Editor>(editor: T): T {
  const {isInline, isVoid, markableVoid} = editor;
  editor.isInline = (element) => {
    const type = (element as CustomElement).type;
    return type === 'link' || type === 'mention' || isInline(element);
  };
  editor.isVoid = (element) => (element as CustomElement).type === 'mention' || isVoid(element);
  editor.markableVoid = (element) =>
    (element as CustomElement).type === 'mention' || markableVoid(element);
  return editor;
}

/** A user offered by the @-mention typeahead. */
export interface MentionCandidate {
  /** User id persisted on the mention node (drives mention notifications). */
  id: string;
  name: string;
  /** Secondary line in the typeahead row (e.g. "Client admin"). */
  description?: string;
}

/**
 * Returns the active @-mention trigger at the collapsed cursor, or null.
 * Mirrors synrgyinfinity's NoteComposer rules: the `@` must be at the start of
 * the text run or preceded by whitespace, and the query may not contain
 * whitespace.
 */
function findMentionTarget(editor: Editor): {target: Range; query: string} | null {
  const {selection} = editor;
  if (!selection || !Range.isCollapsed(selection)) return null;
  const [cursor] = Range.edges(selection);
  const textBefore = Editor.string(editor, {
    anchor: {path: cursor.path, offset: 0},
    focus: cursor,
  });
  const atIndex = textBefore.lastIndexOf('@');
  if (atIndex < 0) return null;
  if (atIndex > 0 && !/\s/.test(textBefore[atIndex - 1])) return null;
  const query = textBefore.slice(atIndex + 1);
  if (/\s/.test(query)) return null;
  return {
    target: {anchor: {path: cursor.path, offset: atIndex}, focus: cursor},
    query,
  };
}

function insertMention(editor: AppEditor, user: MentionCandidate) {
  const mention: MentionElement = {
    type: 'mention',
    userId: user.id,
    userName: user.name,
    children: [{text: ''}],
  };
  Transforms.insertNodes(editor, mention);
  Transforms.move(editor);
  editor.insertText(' ');
}

interface SlateEditorProps {
  value: SlateValue;
  onChange: (value: SlateValue) => void;
  placeholder?: string;
  className?: string;
  /** Min content area height in pixels. Defaults to 250 so it matches the prior MarkdownEditor. */
  minHeight?: number;
  autoFocus?: boolean;
  /** Locks the content and the toolbar (e.g. while the message is saving). */
  readOnly?: boolean;
  /**
   * Users offered by the @-mention typeahead. Omit (or pass an empty list) to
   * disable mentions entirely — typing `@` then behaves as plain text.
   */
  mentionUsers?: MentionCandidate[];
}

export function SlateEditor({
  value,
  onChange,
  placeholder = 'Write your message…',
  className,
  minHeight = 250,
  autoFocus = false,
  readOnly = false,
  mentionUsers,
}: SlateEditorProps) {
  const editor = useMemo(() => withInlines(withHistory(withReact(createEditor()))), []);
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
  const decorate = useCallback((entry: NodeEntry) => decorator(editor, entry), [editor]);

  const mentionsEnabled = !!mentionUsers && mentionUsers.length > 0;
  const [mentionTarget, setMentionTarget] = useState<Range | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionListboxId = useId();

  const mentionMatches = useMemo(() => {
    if (!mentionUsers || !mentionTarget) return [];
    const query = mentionQuery.toLowerCase();
    return mentionUsers.filter((user) => user.name.toLowerCase().includes(query)).slice(0, 8);
  }, [mentionUsers, mentionTarget, mentionQuery]);

  const mentionOpen = !!mentionTarget && mentionMatches.length > 0;

  const handleChange = useCallback(
    (val: SlateValue) => {
      onChange(val);
      if (!mentionsEnabled) return;
      const match = findMentionTarget(editor);
      if (match) {
        setMentionTarget(match.target);
        setMentionQuery(match.query);
        // Content edits re-filter the list, so restart the keyboard highlight.
        // Arrow-key navigation is preventDefault'ed and never lands here.
        setMentionIndex(0);
      } else {
        setMentionTarget(null);
      }
    },
    [editor, onChange, mentionsEnabled],
  );

  const selectMention = useCallback(
    (user: MentionCandidate) => {
      if (!mentionTarget) return;
      Transforms.select(editor, mentionTarget);
      insertMention(editor, user);
      setMentionTarget(null);
      ReactEditor.focus(editor);
    },
    [editor, mentionTarget],
  );

  return (
    <div
      className={cn(
        'slate-editor border border-gray-200 rounded-md overflow-hidden bg-white',
        // pointer-events-none also inerts the toolbar, whose Transforms would
        // otherwise still mutate the value while the Editable is read-only.
        readOnly && 'pointer-events-none opacity-70',
        className,
      )}
    >
      <Slate editor={editor} initialValue={value} onChange={handleChange}>
        <Toolbar />
        <div
          className='prose prose-sm max-w-none px-4 py-3 focus-within:outline-none'
          style={{minHeight}}
        >
          <Editable
            readOnly={readOnly}
            placeholder={placeholder}
            decorate={decorate}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
            autoFocus={autoFocus}
            className='focus:outline-none min-h-full'
            aria-expanded={mentionsEnabled ? mentionOpen : undefined}
            aria-controls={mentionOpen ? mentionListboxId : undefined}
            aria-activedescendant={
              mentionOpen ? `${mentionListboxId}-option-${mentionIndex}` : undefined
            }
            // Candidate clicks preventDefault on mousedown, so a real focus
            // loss (clicking elsewhere) is the only thing that lands here —
            // dismiss the typeahead rather than leave it floating.
            onBlur={() => setMentionTarget(null)}
            onKeyDown={(event) => {
              if (mentionTarget && mentionMatches.length > 0) {
                switch (event.key) {
                  case 'ArrowDown':
                    event.preventDefault();
                    setMentionIndex((i) => (i + 1) % mentionMatches.length);
                    return;
                  case 'ArrowUp':
                    event.preventDefault();
                    setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                    return;
                  case 'Tab':
                  case 'Enter':
                    event.preventDefault();
                    selectMention(mentionMatches[mentionIndex]);
                    return;
                  case 'Escape':
                    event.preventDefault();
                    setMentionTarget(null);
                    return;
                }
              }
              for (const hotkey in HOTKEYS) {
                if (isHotkey(hotkey, event as unknown as KeyboardEvent)) {
                  event.preventDefault();
                  const mark = HOTKEYS[hotkey] as MarkFormat;
                  toggleMark(editor, mark);
                }
              }
            }}
          />
        </div>
        {mentionOpen && mentionTarget ? (
          <MentionTypeahead
            editor={editor}
            target={mentionTarget}
            matches={mentionMatches}
            activeIndex={mentionIndex}
            listboxId={mentionListboxId}
            onHover={setMentionIndex}
            onSelect={selectMention}
          />
        ) : null}
      </Slate>
    </div>
  );
}

interface MentionTypeaheadProps {
  editor: AppEditor;
  target: Range;
  matches: MentionCandidate[];
  activeIndex: number;
  /** DOM id of the listbox, referenced by the Editable's aria-activedescendant. */
  listboxId: string;
  onHover: (index: number) => void;
  onSelect: (user: MentionCandidate) => void;
}

/**
 * Floating list of mention candidates anchored under the active `@` trigger.
 * Rendered in a body portal so overflow-hidden ancestors don't clip it.
 */
function MentionTypeahead({
  editor,
  target,
  matches,
  activeIndex,
  listboxId,
  onHover,
  onSelect,
}: MentionTypeaheadProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Layout effect so the portal is positioned before paint — a passive effect
  // briefly flashes the list at its previous (or unset) coordinates.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.bottom + window.scrollY + 4}px`;
      el.style.left = `${rect.left + window.scrollX}px`;
    } catch {
      // The target range can momentarily desync from the DOM mid-edit; skip
      // repositioning rather than crash — the next render fixes it.
    }
  }, [editor, target, matches.length]);

  return createPortal(
    <div
      ref={ref}
      id={listboxId}
      role='listbox'
      aria-label='Mention suggestions'
      className='absolute z-50 max-h-64 w-64 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg'
      data-testid='mention-typeahead'
    >
      {matches.map((user, index) => (
        <button
          type='button'
          key={user.id}
          id={`${listboxId}-option-${index}`}
          role='option'
          aria-selected={index === activeIndex}
          className={cn(
            'flex w-full items-center gap-2 px-3 py-1.5 text-left',
            index === activeIndex && 'bg-gray-100',
          )}
          onMouseEnter={() => onHover(index)}
          // onMouseDown (not onClick) so the editor doesn't lose focus/selection first.
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(user);
          }}
        >
          <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-navy text-[10px] font-semibold text-white'>
            {initialsFromFullName(user.name)}
          </span>
          <span className='flex min-w-0 flex-col'>
            <span className='truncate text-sm font-medium text-foreground'>{user.name}</span>
            {user.description ? (
              <span className='truncate text-xs text-muted-foreground'>{user.description}</span>
            ) : null}
          </span>
        </button>
      ))}
    </div>,
    document.body,
  );
}

interface SlateReadOnlyProps {
  value?: SlateValue;
  /** If you have the raw stored body (`JSON.stringify(Descendant[])`), pass it as `body`. */
  body?: string | null;
  className?: string;
}

export function SlateReadOnly({value, body, className}: SlateReadOnlyProps) {
  const editor = useMemo(() => withInlines(withReact(createEditor())), []);
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
  const decorate = useCallback((entry: NodeEntry) => decorator(editor, entry), [editor]);

  const initialValue = useMemo<SlateValue>(() => {
    if (value && value.length > 0) return value;
    if (body) {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed as SlateValue;
      } catch {
        // Not valid Slate JSON — fall back to rendering as plain text.
      }
      return plainTextToSlate(body);
    }
    return [{type: 'paragraph', children: [{text: ''}]}];
  }, [value, body]);

  return (
    <div className={cn('prose prose-sm max-w-none text-brand-navy', className)}>
      <Slate editor={editor} initialValue={initialValue}>
        <Editable
          readOnly
          decorate={decorate}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          className='focus:outline-none'
        />
      </Slate>
    </div>
  );
}

function Toolbar() {
  return (
    <div className='flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50/60 px-2 py-1.5'>
      <MarkButton format='bold' icon={<Bold />} label='Bold' />
      <MarkButton format='italic' icon={<Italic />} label='Italic' />
      <MarkButton format='underline' icon={<Underline />} label='Underline' />
      <MarkButton format='code' icon={<Code />} label='Code' />
      <LinkButton />
      <Separator orientation='vertical' className='mx-1 h-6' />
      <BlockButton format='heading-one' icon={<Heading1 />} label='Heading 1' />
      <BlockButton format='heading-two' icon={<Heading2 />} label='Heading 2' />
      <BlockButton format='block-quote' icon={<Quote />} label='Quote' />
      <Separator orientation='vertical' className='mx-1 h-6' />
      <BlockButton format='bulleted-list' icon={<List />} label='Bulleted list' />
      <BlockButton format='numbered-list' icon={<ListOrdered />} label='Numbered list' />
      <Separator orientation='vertical' className='mx-1 h-6' />
      <BlockButton format='left' icon={<AlignLeft />} label='Align left' />
      <BlockButton format='center' icon={<AlignCenter />} label='Align center' />
      <BlockButton format='right' icon={<AlignRight />} label='Align right' />
      <BlockButton format='justify' icon={<AlignJustify />} label='Justify' />
    </div>
  );
}

function MarkButton({format, icon, label}: {format: MarkFormat; icon: ReactNode; label: string}) {
  const editor = useSlate();
  const active = isMarkActive(editor, format);
  return (
    <Toggle
      size='sm'
      pressed={active}
      aria-label={label}
      title={label}
      onPressedChange={() => toggleMark(editor, format)}
      className={cn(
        'data-[state=on]:bg-brand-navy data-[state=on]:text-white text-brand-gray hover:text-brand-navy',
      )}
      onMouseDown={(event) => event.preventDefault()}
    >
      {icon}
    </Toggle>
  );
}

function BlockButton({format, icon, label}: {format: BlockFormat; icon: ReactNode; label: string}) {
  const editor = useSlate();
  const blockType = isTextAlign(format) ? 'align' : 'type';
  const active = isBlockActive(editor, format, blockType);
  return (
    <Toggle
      size='sm'
      pressed={active}
      aria-label={label}
      title={label}
      onPressedChange={() => toggleBlock(editor, format)}
      className={cn(
        'data-[state=on]:bg-brand-navy data-[state=on]:text-white text-brand-gray hover:text-brand-navy',
      )}
      onMouseDown={(event) => event.preventDefault()}
    >
      {icon}
    </Toggle>
  );
}

/**
 * Toolbar button that opens an insert/edit hyperlink dialog. The editor
 * selection is captured on open (the dialog steals focus) and restored before
 * the link is applied.
 */
function LinkButton() {
  const editor = useSlate() as AppEditor;
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const savedSelection = useRef<Range | null>(null);
  const active = isLinkActive(editor);

  const openDialog = () => {
    savedSelection.current = editor.selection;
    const entry = getActiveLinkEntry(editor);
    if (entry) {
      setUrl(entry[0].url);
      setText(SlateNode.string(entry[0]));
    } else {
      setUrl('');
      setText(
        editor.selection && Range.isExpanded(editor.selection)
          ? Editor.string(editor, editor.selection)
          : '',
      );
    }
    setOpen(true);
  };

  const restoreSelection = () => {
    ReactEditor.focus(editor);
    if (savedSelection.current) {
      Transforms.select(editor, savedSelection.current);
    } else {
      Transforms.select(editor, Editor.end(editor, []));
    }
  };

  const applyLink = (event: FormEvent) => {
    event.preventDefault();
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    const normalizedUrl = /^(https?:|mailto:|tel:)/i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;

    restoreSelection();
    // When editing an existing link, select the whole node so insertLink
    // replaces it instead of nesting.
    const entry = getActiveLinkEntry(editor);
    if (entry) Transforms.select(editor, entry[1]);
    insertLink(editor, normalizedUrl, text.trim());
    setOpen(false);
  };

  const removeLink = () => {
    restoreSelection();
    unwrapLink(editor);
    setOpen(false);
  };

  return (
    <>
      <Toggle
        size='sm'
        pressed={active}
        aria-label='Insert link'
        title='Insert link'
        onPressedChange={openDialog}
        className={cn(
          'data-[state=on]:bg-brand-navy data-[state=on]:text-white text-brand-gray hover:text-brand-navy',
        )}
        onMouseDown={(event) => event.preventDefault()}
      >
        <LinkIcon />
      </Toggle>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{active ? 'Edit Link' : 'Insert Link'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={applyLink} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='slate-link-url'>URL</Label>
              <Input
                id='slate-link-url'
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder='https://example.com'
                autoFocus
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='slate-link-text'>Link text (optional)</Label>
              <Input
                id='slate-link-text'
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder='Display text'
              />
            </div>
            <DialogFooter>
              {active && (
                <Button type='button' variant='destructive' onClick={removeLink}>
                  Remove Link
                </Button>
              )}
              <Button type='button' variant='outline' onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type='submit' disabled={!url.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function isLinkActive(editor: AppEditor): boolean {
  return getActiveLinkEntry(editor) !== null;
}

function getActiveLinkEntry(editor: AppEditor): NodeEntry<LinkElement> | null {
  const {selection} = editor;
  if (!selection) return null;
  const [entry] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && (n as CustomElement).type === 'link',
    }),
  );
  return (entry as NodeEntry<LinkElement>) ?? null;
}

function unwrapLink(editor: AppEditor) {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && (n as CustomElement).type === 'link',
  });
}

function insertLink(editor: AppEditor, url: string, text: string) {
  if (isLinkActive(editor)) unwrapLink(editor);

  const {selection} = editor;
  const isCollapsed = !selection || Range.isCollapsed(selection);
  const display = text || url;

  if (isCollapsed || text) {
    // No selection to wrap, or the user provided explicit display text —
    // replace whatever is selected with a fresh link node.
    if (!isCollapsed) Transforms.delete(editor);
    const link: LinkElement = {type: 'link', url, children: [{text: display}]};
    Transforms.insertNodes(editor, link);
    Transforms.move(editor, {unit: 'offset'});
  } else {
    const link: LinkElement = {type: 'link', url, children: []};
    Transforms.wrapNodes(editor, link, {split: true});
    Transforms.collapse(editor, {edge: 'end'});
  }
}

function isTextAlign(format: BlockFormat): format is TextAlign {
  return (TEXT_ALIGN_TYPES as readonly string[]).includes(format);
}

function toggleBlock(editor: AppEditor, format: BlockFormat) {
  const blockKey = isTextAlign(format) ? 'align' : 'type';
  const active = isBlockActive(editor, format, blockKey);
  const isList = (LIST_TYPES as readonly string[]).includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (LIST_TYPES as readonly string[]).includes((n as CustomElement).type) &&
      !isTextAlign(format),
    split: true,
  });

  let newProperties: Partial<CustomElement>;
  if (isTextAlign(format)) {
    newProperties = {align: active ? undefined : (format as TextAlign)};
  } else if (isList) {
    newProperties = {type: active ? 'paragraph' : 'list-item'};
  } else {
    newProperties = {
      type: active ? 'paragraph' : (format as CustomElement['type']),
    };
  }
  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!active && isList) {
    const block: CustomElement = {
      type: format as 'numbered-list' | 'bulleted-list',
      children: [],
    };
    Transforms.wrapNodes(editor, block);
  }
}

function toggleMark(editor: AppEditor, format: MarkFormat) {
  const active = isMarkActive(editor, format);
  if (active) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
}

function isBlockActive(editor: AppEditor, format: BlockFormat, blockKey: 'type' | 'align') {
  const {selection} = editor;
  if (!selection) return false;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;
        const node = n as CustomElement;
        return (node as unknown as Record<string, unknown>)[blockKey] === format;
      },
    }),
  );
  return !!match;
}

function isMarkActive(editor: AppEditor, format: MarkFormat) {
  const marks = Editor.marks(editor) as Record<string, unknown> | null;
  return marks ? marks[format] === true : false;
}

function Element({attributes, children, element}: RenderElementProps) {
  const node = element as CustomElement;
  if (node.type === 'mention') {
    return (
      <span
        {...attributes}
        contentEditable={false}
        data-user-id={node.userId}
        className='rounded bg-blue-50 px-1 py-0 text-[0.9em] font-medium text-blue-600'
      >
        @{node.userName}
        {children}
      </span>
    );
  }
  if (node.type === 'link') {
    return (
      <a
        {...attributes}
        href={node.url}
        target='_blank'
        rel='noreferrer'
        title={node.url}
        className='text-brand-blue underline'
      >
        {children}
      </a>
    );
  }
  const style = node.align ? {textAlign: node.align} : undefined;
  switch (node.type) {
    case 'block-quote':
      return (
        <blockquote
          className='border-l-4 border-brand-blue/60 pl-3 italic text-brand-navy/80'
          style={style}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul className='list-inside list-disc pl-2' style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 className='text-2xl font-semibold text-brand-navy' style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 className='text-xl font-semibold text-brand-navy' style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol className='list-inside list-decimal pl-2' style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'paragraph':
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
}

interface LinkLeaf extends RenderLeafProps {
  leaf: RenderLeafProps['leaf'] & {
    decoration?: 'link';
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    code?: boolean;
  };
}

function Leaf({attributes, children, leaf}: LinkLeaf) {
  let rendered: ReactNode = children;
  if (leaf.decoration === 'link') {
    // URL_REGEX also matches scheme-less values like `www.example.com`; a
    // bare host in href would resolve as a relative path, so prefix a scheme.
    const href = /^(https?|ftp|file):/i.test(leaf.text) ? leaf.text : `https://${leaf.text}`;
    rendered = (
      <a className='text-brand-blue underline' href={href} rel='noreferrer' target='_blank'>
        {rendered}
      </a>
    );
  }
  if (leaf.bold) rendered = <strong>{rendered}</strong>;
  if (leaf.code)
    rendered = <code className='rounded-sm bg-gray-100 px-1 py-0.5 text-[0.9em]'>{rendered}</code>;
  if (leaf.italic) rendered = <em>{rendered}</em>;
  if (leaf.underline) rendered = <u>{rendered}</u>;
  return <span {...attributes}>{rendered}</span>;
}

const URL_REGEX =
  /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim;

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

/**
 * Render legacy plain-text / lightweight-markdown bodies as Slate paragraphs.
 * - `[label](url)` collapses to `label: url` so the URL_REGEX-based decorator
 *   surfaces the link.
 * - Blank lines split paragraphs; single newlines stay inside a paragraph.
 */
function plainTextToSlate(body: string): SlateValue {
  const normalized = body.replace(MARKDOWN_LINK_REGEX, '$1: $2');
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (paragraphs.length === 0) return [{type: 'paragraph', children: [{text: ''}]}];
  return paragraphs.map((text) => ({
    type: 'paragraph',
    children: [{text}],
  }));
}

function decorator(editor: Editor, [node, path]: NodeEntry) {
  const ranges: Array<Range & {decoration: 'link'}> = [];
  const text = (node as {text?: string}).text;
  if (typeof text !== 'string' || !text) return ranges;
  // Text inside an explicit link element is already an anchor — decorating it
  // would nest <a> tags.
  if (path.length > 0) {
    const parent = SlateNode.parent(editor, path);
    if (SlateElement.isElement(parent) && (parent as CustomElement).type === 'link') {
      return ranges;
    }
  }
  const matches = text.match(URL_REGEX);
  if (!matches) return ranges;
  for (const url of matches) {
    const trimmed = url.trim();
    const offset = text.indexOf(trimmed);
    if (offset < 0) continue;
    ranges.push({
      anchor: {path, offset},
      focus: {path, offset: offset + trimmed.length},
      decoration: 'link',
    });
  }
  return ranges;
}

export type {Descendant};
